import React from 'react';
import type { Column } from 'react-table';
import type { Tag as TagType } from '@whizz/api-model/dist/@types/endpoints/Tags';
import { useI18n } from '@whizz/react-i18n';
import { ButtonIcon, Loader, Table, Tag } from '../../../../../components';
import { Edit } from '../../Classes/modules/ClassesTable.components';
import { createGlobalStyle } from 'styled-components';
import { GLOBAL_ICONS } from '../../../../../util/vars';

const GlobalStyle = createGlobalStyle`
  table {

    [data-id='col_id'] {
      width: 120px;
      text-align: left;
    }

    [data-id='col_abbreviation'] {
      justify-content: center;
      text-align:center;
    }

    [data-id='col_description'] {
      width: 50%;
    }

    [data-id='col_studentCount'] {
      width: 120px;
      text-align: center;
    }

  }
`;

const TagsBlock = ({ tags, handleEdit, handleDelete, isLoading }: Props): JSX.Element => {
  const { translate } = useI18n();

  const defaultTags = tags.filter((tag: TagType) => tag.isDefault);
  const schoolTags = tags.filter((tag: TagType) => !tag.isDefault);

  if (isLoading) {
    return (
      <Loader />
    );
  }

  const columns: Column<TagType>[] = [
    {
      Header: '',
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="flex items-center justify-start">
          <Edit onClick={() => handleEdit(row.original, row.original.isDefault)} />
        </div>
      ),
    },
    {
      Header: translate('pages.ROSTER_MANAGEMENT.TAGS.TABLE.TAG') as string,
      accessor: 'abbreviation',
      Cell: ({ row, value }) => (
        <Tag color={row.original.color}>
          {row.original.isDefault
            ? translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_ABBREVIATIONS.${value}`, { defaultValue: value })
            : value}
        </Tag>
      ),
    },
    {
      Header: translate('pages.ROSTER_MANAGEMENT.TAGS.TABLE.DESCRIPTION') as string,
      accessor: 'description',
      Cell: ({ row, value }) =>
        row.original.isDefault
          ? translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_DESCRIPTIONS.${row.original.abbreviation}`, { defaultValue: value })
          : value,
    },
    {
      Header: translate('pages.ROSTER_MANAGEMENT.TAGS.TABLE.STUDENT_COUNT') as string,
      accessor: 'studentCount',
      Cell: ({ value }) => (
        <div className="text-center w-full">{value ?? 0}</div>
      ),
    },
  ];

  return (
    <>
      <GlobalStyle />

      {/* Default Tags */}
      <div className="space-y-3" data-cy="rm-tags-default">
        <h2 className="font-bold">
          {translate('pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS')}
        </h2>

        <Table
          id="default-tags"
          cols={columns}
          content={defaultTags}
        />
      </div>

      {/* Custom Tags */}
      {schoolTags.length > 0 ? (
        <div className="space-y-3 mt-8">
          <h2 className="font-bold">
            {translate('pages.ROSTER_MANAGEMENT.TAGS.CUSTOM_TAGS')}
          </h2>

          <Table
            id="custom-tags"
            cols={columns}
            content={schoolTags}
          />
        </div>
      ) : null}
    </>
  );
};

export default TagsBlock;

interface Props {
  tags: TagType[];
  handleEdit: (tag: TagType, isDefault?: boolean) => void;
  handleDelete: (tag: TagType) => void;
  isLoading: boolean;
}
